import React from "react";
import { useParams } from "react-router-dom";
import DashboardLayout from "@/components/DashboardLayout";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { AlertTriangle, Sparkles, Loader2, Radar } from "lucide-react";
import { logger } from "@/lib/logger";

const CATEGORIES = ["prop", "wardrobe", "time", "location", "name", "character", "dialogue", "other"];

export default function ContinuityPage() {
  const { projectId } = useParams();
  const [report, setReport] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [filter, setFilter] = React.useState("all");
  const { updateCredits } = useAuth();

  React.useEffect(() => {
    api.get(`/continuity/latest/${projectId}`)
      .then((r) => { if (r.data?.flags) setReport(r.data); })
      .catch((err) => logger.warn("continuity latest failed", err));
  }, [projectId]);

  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/continuity/scan", { project_id: projectId });
      setReport(data.report);
      updateCredits(data.credits);
      toast.success(`Scan complete — ${data.report.flags?.length || 0} flag${data.report.flags?.length === 1 ? "" : "s"}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Scan failed");
    } finally {
      setBusy(false);
    }
  };

  const flags = (report?.flags || []).filter((f) => filter === "all" || f.category === filter);

  return (
    <DashboardLayout>
      <div className="max-w-[1200px] mx-auto px-8 py-10">
        <div className="flex items-baseline justify-between mb-6">
          <div>
            <div className="uppercase text-xs tracking-[0.3em] text-[#D4AF37] mb-2">Continuity Radar</div>
            <h1 className="font-editorial text-4xl">What might trip up production</h1>
          </div>
          <button onClick={run} disabled={busy}
            className="bg-[#D4AF37] text-[#0A0A0A] px-5 py-2.5 text-sm font-medium hover:bg-[#F59E0B] inline-flex items-center gap-2 disabled:opacity-50 transition-colors"
            data-testid="continuity-run">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
            {report ? "Re-scan" : "Scan screenplay"}
          </button>
        </div>

        {!report ? (
          <div className="border border-[#1E1E1E] bg-[#0F0F10] p-12 text-center">
            <Radar size={32} strokeWidth={1.5} className="mx-auto text-[#D4AF37]" />
            <div className="font-editorial text-2xl mt-3">No scan yet.</div>
            <p className="text-sm text-[#A1A1AA] mt-2 max-w-md mx-auto">
              Run the radar to flag wardrobe, prop, time-of-day, location and dialogue inconsistencies before production catches them.
            </p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] gap-6 mb-6">
              <div className="border border-[#1E1E1E] bg-[#141414] p-6 flex flex-col items-center text-center">
                <div className="text-[10px] uppercase tracking-widest text-[#D4AF37]">Clean score</div>
                <div className={`font-editorial text-6xl mt-3 ${scoreColor(report.clean_score)}`} data-testid="continuity-score">{report.clean_score}</div>
                <div className="text-xs text-[#71717A]">of 100</div>
                <div className="mt-4 text-xs text-[#A1A1AA]">
                  {report.clean_score >= 90 ? "Nearly spotless." :
                   report.clean_score >= 70 ? "Solid — a few things to review." :
                   report.clean_score >= 50 ? "Multiple continuity risks flagged." :
                   "High risk. Address before production."}
                </div>
              </div>

              <div className="border border-[#1E1E1E] bg-[#0F0F10] p-6">
                <div className="text-[10px] uppercase tracking-widest text-[#D4AF37] mb-3">By category</div>
                <div className="grid grid-cols-4 md:grid-cols-8 gap-2 text-xs">
                  {CATEGORIES.map((c) => {
                    const count = (report.flags || []).filter((f) => f.category === c).length;
                    return (
                      <button key={c} onClick={() => setFilter(filter === c ? "all" : c)}
                        className={`p-2 border ${filter === c ? "border-[#D4AF37] bg-[#141414]" : "border-[#27272A]"} hover:border-[#A1A1AA] transition-colors`}
                        data-testid={`continuity-cat-${c}`}>
                        <div className="capitalize text-[10px] text-[#A1A1AA]">{c}</div>
                        <div className="font-editorial text-lg text-[#FFFFF0]">{count}</div>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            {flags.length === 0 ? (
              <div className="border border-[#1E1E1E] bg-[#0F0F10] p-10 text-center">
                <div className="font-editorial text-2xl">No flags in this view.</div>
                <p className="text-sm text-[#A1A1AA] mt-2">Try clearing the filter or run another scan.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {flags.map((f, i) => (
                  <div key={f.id || `flag-${i}-${(f.title || '').slice(0, 20)}`} className="border border-[#1E1E1E] bg-[#141414] p-5" data-testid={`continuity-flag-${i}`}>
                    <div className="flex items-start gap-4">
                      <div className={`shrink-0 w-9 h-9 border flex items-center justify-center ${severityBorder(f.severity)}`}>
                        <AlertTriangle size={14} className={severityText(f.severity)} />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-baseline justify-between gap-4">
                          <div className="font-editorial text-xl">{f.title}</div>
                          <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest">
                            <span className={severityText(f.severity)}>{f.severity}</span>
                            <span className="text-[#71717A]">/</span>
                            <span className="text-[#A1A1AA]">{f.category}</span>
                          </div>
                        </div>
                        {(f.affected_scenes || []).length > 0 && (
                          <div className="text-xs text-[#71717A] font-mono-editor mt-1">{(f.affected_scenes || []).join(" · ")}</div>
                        )}
                        <div className="text-sm text-[#E4E4E7] mt-2 leading-relaxed">{f.description}</div>
                        {f.evidence && <div className="text-xs text-[#A1A1AA] italic mt-2 border-l-2 border-[#27272A] pl-3">{f.evidence}</div>}
                        {f.recommendation && (
                          <div className="mt-3 text-xs text-[#A1A1AA]">
                            <span className="text-[#D4AF37]">Fix:</span> {f.recommendation}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </DashboardLayout>
  );
}

function scoreColor(v) {
  if (v == null) return "text-[#71717A]";
  if (v >= 85) return "text-[#D4AF37]";
  if (v >= 60) return "text-[#F59E0B]";
  return "text-[#7F1D1D]";
}
function severityText(s) { return { high: "text-[#7F1D1D]", medium: "text-[#F59E0B]", low: "text-[#A1A1AA]" }[s] || "text-[#A1A1AA]"; }
function severityBorder(s) { return { high: "border-[#7F1D1D]", medium: "border-[#F59E0B]", low: "border-[#27272A]" }[s] || "border-[#27272A]"; }
